import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, X, Save, Clock, CalendarDays, Coins, Building2 } from "lucide-react";
import { DAYS } from "./shared";

const PERIODICITY_OPTIONS = [
  ["permanent", "Permanent"],
  ["daily", "Tous les jours"],
  ["weekly", "Chaque semaine"],
  ["monthly", "Chaque mois"],
  ["yearly", "Chaque année"],
];
const PERIODICITY_LABEL = Object.fromEntries(PERIODICITY_OPTIONS);
const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
const DAY_LABEL = Object.fromEntries(DAYS);

const pad = (n) => String(n).padStart(2, "0");
const mmdd = (mo, da) => `${pad(mo)}-${pad(da)}`;
const parseMMDD = (v) => {
  if (!v || typeof v !== "string") return [null, null];
  const [mo, da] = v.split("-").map((x) => parseInt(x, 10));
  return [mo || null, da || null];
};
const fmtMMDD = (v) => {
  const [mo, da] = parseMMDD(v);
  return mo && da ? `${da} ${MONTHS[mo - 1].toLowerCase()}` : "—";
};

const DAY_ORDER = Object.fromEntries(DAYS.map(([k], i) => [k, i]));

const blankForm = (locationId) => ({
  name: "",
  amount: "",
  currency: "EUR",
  periodicity: "permanent",
  weekdays: [],
  month_days: [],
  year_mode: "date",
  year_date: "12-25",
  year_start: "07-01",
  year_end: "08-31",
  time_type: "all_day",
  start_time: "18:00",
  end_time: "22:00",
  agency_ids: [locationId],
});

function Field({ label, required, children, hint }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-700 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </div>
      {children}
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

function MonthDayPicker({ value, onChange, testid }) {
  const [mo, da] = parseMMDD(value);
  return (
    <div className="flex items-center gap-2">
      <select
        value={mo || ""}
        onChange={(e) => onChange(mmdd(Number(e.target.value), da || 1))}
        className="h-9 px-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-violet-500"
        data-testid={`${testid}-month`}
      >
        {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
      </select>
      <select
        value={da || ""}
        onChange={(e) => onChange(mmdd(mo || 1, Number(e.target.value)))}
        className="h-9 px-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-violet-500"
        data-testid={`${testid}-day`}
      >
        {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
    </div>
  );
}

function SurchargeForm({ location, allLocations, initial, onCancel, onSaved }) {
  const [f, setF] = useState(() => {
    const base = blankForm(location.id);
    if (!initial) return base;
    const nonNull = Object.fromEntries(Object.entries(initial).filter(([, v]) => v !== null && v !== undefined));
    return { ...base, ...nonNull };
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const otherLocations = allLocations.filter((l) => l.id !== location.id);
  const allSelected = allLocations.length > 0 && allLocations.every((l) => f.agency_ids.includes(l.id));

  const toggleWeekday = (key) =>
    set("weekdays", f.weekdays.includes(key) ? f.weekdays.filter((d) => d !== key) : [...f.weekdays, key]);
  const toggleMonthDay = (d) =>
    set("month_days", f.month_days.includes(d) ? f.month_days.filter((x) => x !== d) : [...f.month_days, d]);
  const toggleAgency = (id) => {
    if (id === location.id) return; // courante toujours incluse
    set("agency_ids", f.agency_ids.includes(id) ? f.agency_ids.filter((a) => a !== id) : [...f.agency_ids, id]);
  };
  const toggleAll = () => {
    if (allSelected) set("agency_ids", [location.id]);
    else set("agency_ids", allLocations.map((l) => l.id));
  };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Le nom du surcoût est requis."); return; }
    if (f.amount === "" || Number(f.amount) < 0 || isNaN(Number(f.amount))) { toast.error("Montant invalide."); return; }
    if (f.periodicity === "weekly" && f.weekdays.length === 0) { toast.error("Sélectionnez au moins un jour de la semaine."); return; }
    if (f.periodicity === "monthly" && f.month_days.length === 0) { toast.error("Sélectionnez au moins un jour du mois."); return; }
    setSaving(true);
    try {
      const payload = { ...f, amount: Number(f.amount), location_id: location.id };
      if (initial?.id) await api.patch(`/vendor/surcharges/${initial.id}`, payload);
      else await api.post(`/vendor/surcharges`, payload);
      toast.success(initial?.id ? "Surcoût modifié" : "Surcoût ajouté");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="surcharge-form-modal">
      <div className="fixed inset-0 bg-violet-950/60 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative w-full max-w-lg max-h-[88vh] overflow-y-auto rounded-3xl bg-white shadow-2xl p-6">
        <button onClick={onCancel} className="absolute top-4 right-4 p-1.5 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors" data-testid="surcharge-form-close" aria-label="Fermer">
          <X className="h-4 w-4" />
        </button>
        <h3 className="font-heading text-lg font-semibold text-slate-900">
          {initial?.id ? "Modifier le surcoût tarifaire" : "Ajouter un surcoût tarifaire"}
        </h3>

        <div className="mt-5 space-y-4">
          <Field label="Nom" required>
            <input
              value={f.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Ex : Supplément de nuit, Supplément dimanche…"
              className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
              data-testid="surcharge-name"
            />
          </Field>

          <Field label="Montant" required>
            <div className="relative w-44">
              <input
                type="number" min="0" step="0.01"
                value={f.amount}
                onChange={(e) => set("amount", e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="0,00"
                className="w-full h-10 px-3 pr-8 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-violet-500"
                data-testid="surcharge-amount"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-semibold pointer-events-none">€</span>
            </div>
          </Field>

          <Field label="Périodicité" required>
            <select
              value={f.periodicity}
              onChange={(e) => set("periodicity", e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-violet-500"
              data-testid="surcharge-periodicity"
            >
              {PERIODICITY_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>

          {f.periodicity === "weekly" && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="surcharge-weekdays">
              <div className="text-[11px] font-semibold text-slate-600 mb-2">Jours de la semaine concernés</div>
              <div className="flex flex-wrap gap-1.5">
                {DAYS.map(([key, label]) => (
                  <button
                    key={key} type="button" onClick={() => toggleWeekday(key)}
                    className={`px-3 h-8 rounded-full text-xs font-semibold transition-colors ${f.weekdays.includes(key) ? "bg-violet-600 text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-violet-400"}`}
                    data-testid={`surcharge-weekday-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {f.periodicity === "monthly" && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="surcharge-monthdays">
              <div className="text-[11px] font-semibold text-slate-600 mb-2">Jours du mois concernés</div>
              <div className="flex flex-wrap gap-1">
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <button
                    key={d} type="button" onClick={() => toggleMonthDay(d)}
                    className={`h-8 w-8 rounded-lg text-xs font-semibold transition-colors ${f.month_days.includes(d) ? "bg-violet-600 text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-violet-400"}`}
                    data-testid={`surcharge-monthday-${d}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-slate-400 mt-2">Si le jour n'existe pas dans le mois, l'occurrence n'est pas reportée.</div>
            </div>
          )}

          {f.periodicity === "yearly" && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="surcharge-yearly">
              <div className="flex gap-2">
                <button type="button" onClick={() => set("year_mode", "date")} className={`flex-1 h-9 rounded-lg text-xs font-semibold transition-colors ${f.year_mode === "date" ? "bg-violet-600 text-white" : "bg-white border border-slate-200 text-slate-700"}`} data-testid="surcharge-year-mode-date">Date précise</button>
                <button type="button" onClick={() => set("year_mode", "period")} className={`flex-1 h-9 rounded-lg text-xs font-semibold transition-colors ${f.year_mode === "period" ? "bg-violet-600 text-white" : "bg-white border border-slate-200 text-slate-700"}`} data-testid="surcharge-year-mode-period">Période récurrente</button>
              </div>
              {f.year_mode === "date" ? (
                <Field label="Date annuelle">
                  <MonthDayPicker value={f.year_date} onChange={(v) => set("year_date", v)} testid="surcharge-year-date" />
                </Field>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Du">
                    <MonthDayPicker value={f.year_start} onChange={(v) => set("year_start", v)} testid="surcharge-year-start" />
                  </Field>
                  <Field label="Au" hint="Peut traverser le changement d'année (ex : 20 déc → 5 jan).">
                    <MonthDayPicker value={f.year_end} onChange={(v) => set("year_end", v)} testid="surcharge-year-end" />
                  </Field>
                </div>
              )}
            </div>
          )}

          <Field label="Horaire" required>
            <select
              value={f.time_type}
              onChange={(e) => set("time_type", e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-violet-500"
              data-testid="surcharge-time-type"
            >
              <option value="all_day">Toute la journée</option>
              <option value="range">Plage d'heures</option>
            </select>
          </Field>

          {f.time_type === "range" && (
            <div data-testid="surcharge-time-range">
              <div className="flex items-center gap-3">
                <div>
                  <div className="text-xs font-semibold text-slate-700 mb-1.5">Heure de début</div>
                  <input type="time" value={f.start_time} onChange={(e) => set("start_time", e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-violet-500" data-testid="surcharge-start-time" />
                </div>
                <span className="mt-6 text-slate-400">→</span>
                <div>
                  <div className="text-xs font-semibold text-slate-700 mb-1.5">Heure de fin</div>
                  <input type="time" value={f.end_time} onChange={(e) => set("end_time", e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-violet-500" data-testid="surcharge-end-time" />
                </div>
              </div>
              <div className="text-[11px] text-slate-400 mt-1.5">Format 24 h. Peut traverser minuit (ex : 22:00 → 06:00).</div>
            </div>
          )}

          <Field label="Appliquer le surcoût tarifaire à d'autres agences" hint="L'agence en cours de configuration est toujours incluse.">
            <div className="rounded-xl border border-slate-200 bg-white p-2 space-y-1 max-h-44 overflow-y-auto" data-testid="surcharge-agencies">
              <label className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-violet-600" data-testid="surcharge-agency-all" />
                <span className="text-sm font-semibold text-slate-800">Toutes les agences</span>
              </label>
              <label className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-violet-50/60 cursor-not-allowed">
                <input type="checkbox" checked disabled className="accent-violet-600" />
                <span className="text-sm text-slate-700">{location.name} <span className="text-[10px] text-violet-600 font-semibold">(agence courante)</span></span>
              </label>
              {otherLocations.map((l) => (
                <label key={l.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={f.agency_ids.includes(l.id)} onChange={() => toggleAgency(l.id)} className="accent-violet-600" data-testid={`surcharge-agency-${l.id}`} />
                  <span className="text-sm text-slate-700">{l.name}</span>
                </label>
              ))}
              {otherLocations.length === 0 && (
                <div className="px-2 py-1.5 text-xs text-slate-400">Aucune autre agence pour cette entreprise.</div>
              )}
            </div>
          </Field>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 h-10 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors" data-testid="surcharge-cancel">Annuler</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 h-10 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 transition-colors" data-testid="surcharge-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

function periodicitySummary(s) {
  if (s.periodicity === "weekly") return [...(s.weekdays || [])].sort((a, b) => (DAY_ORDER[a] ?? 99) - (DAY_ORDER[b] ?? 99)).map((d) => DAY_LABEL[d] || d).join(", ") || "—";
  if (s.periodicity === "monthly") return "Le " + (s.month_days || []).join(", ") + " du mois";
  if (s.periodicity === "yearly") {
    if (s.year_mode === "period") return `${fmtMMDD(s.year_start)} → ${fmtMMDD(s.year_end)}`;
    return fmtMMDD(s.year_date);
  }
  return null;
}

export default function SurchargesSection({ location }) {
  const [surcharges, setSurcharges] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/vendor/locations/${location.id}/surcharges`)
      .then((r) => setSurcharges(r.data.surcharges || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [location.id]);

  useEffect(() => {
    load();
    api.get("/vendor/locations").then((r) => setAllLocations(r.data.locations || [])).catch(() => {});
  }, [load]);

  const locName = (id) => allLocations.find((l) => l.id === id)?.name || "Agence";

  const confirmDelete = async (mode) => {
    const s = deleteTarget;
    try {
      const url = mode === "detach"
        ? `/vendor/surcharges/${s.id}?detach_location_id=${location.id}`
        : `/vendor/surcharges/${s.id}`;
      await api.delete(url);
      toast.success(mode === "detach" ? "Surcoût retiré de cette agence" : "Surcoût supprimé");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-[11px] font-semibold text-slate-600">Surcoûts tarifaires</div>
        <button
          onClick={() => { setEditing(null); setFormOpen(true); }}
          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 transition-colors"
          data-testid="surcharge-add-btn"
        >
          <Plus className="h-3.5 w-3.5" /> Ajouter un surcoût tarifaire
        </button>
      </div>

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>
      ) : surcharges.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-5 text-center text-xs text-slate-400" data-testid="surcharge-empty">
          Aucun surcoût tarifaire configuré pour cette agence.
        </div>
      ) : (
        <div className="space-y-2" data-testid="surcharge-list">
          {surcharges.map((s) => {
            const summary = periodicitySummary(s);
            return (
              <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`surcharge-item-${s.id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900">{s.name}</span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-[11px] font-bold">
                        <Coins className="h-3 w-3" /> {Number(s.amount).toFixed(2).replace(".", ",")} €
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                      <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {PERIODICITY_LABEL[s.periodicity]}{summary ? ` · ${summary}` : ""}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {s.time_type === "range" ? `${s.start_time} → ${s.end_time}` : "Toute la journée"}</span>
                      <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> {(s.agency_ids || []).length > 1 ? `${s.agency_ids.length} agences` : locName((s.agency_ids || [])[0])}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => { setEditing(s); setFormOpen(true); }} className="h-8 w-8 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-violet-600 flex items-center justify-center" data-testid={`surcharge-edit-${s.id}`} title="Modifier">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => setDeleteTarget(s)} className="h-8 w-8 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 flex items-center justify-center" data-testid={`surcharge-delete-${s.id}`} title="Supprimer">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {formOpen && (
        <SurchargeForm
          location={location}
          allLocations={allLocations}
          initial={editing}
          onCancel={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); load(); }}
        />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="surcharge-delete-modal">
          <div className="fixed inset-0 bg-violet-950/60 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl p-6">
            <h3 className="font-heading text-lg font-semibold text-slate-900">Supprimer « {deleteTarget.name} » ?</h3>
            {(deleteTarget.agency_ids || []).length > 1 ? (
              <>
                <p className="text-sm text-slate-600 mt-2">Ce surcoût est partagé avec <b>{deleteTarget.agency_ids.length} agences</b>. Choisissez la portée de la suppression :</p>
                <div className="mt-5 space-y-2">
                  <button onClick={() => confirmDelete("detach")} className="w-full h-10 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors" data-testid="surcharge-delete-detach">
                    Retirer uniquement de « {location.name} »
                  </button>
                  <button onClick={() => confirmDelete("all")} className="w-full h-10 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors" data-testid="surcharge-delete-all">
                    Supprimer de toutes les agences
                  </button>
                  <button onClick={() => setDeleteTarget(null)} className="w-full h-10 rounded-xl text-sm font-semibold text-slate-500 hover:bg-slate-50 transition-colors" data-testid="surcharge-delete-cancel">
                    Annuler
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-slate-600 mt-2">Cette action est définitive.</p>
                <div className="mt-5 flex justify-end gap-2">
                  <button onClick={() => setDeleteTarget(null)} className="px-4 h-10 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors" data-testid="surcharge-delete-cancel">Annuler</button>
                  <button onClick={() => confirmDelete("all")} className="px-5 h-10 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors" data-testid="surcharge-delete-all">Supprimer</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
